import React from "react";

import {
  AccountCircleRounded,
  ArrowForwardRounded,
  DataObjectRounded,
  KeyboardArrowDownRounded,
  MenuRounded,
  RefreshRounded,
} from "@mui/icons-material";
import { Box, Divider, IconButton, Skeleton } from "@mui/material";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";

import useMainState from "../../../config/context/mainStateContext";
import useModal from "../../../config/context/modalContext";
import { GreyPallet } from "../../../config/styles";
import { Dev } from "../../../config/util/consoleUtil";

import MainSolidButton from "../../../components/MainSolidButton";
import MainText from "../../../components/MainText";
import PageHeader from "../../../components/PageHeader";
import { ErrorLogService } from "../../../config/services/ErrorLogService";

const listOfFunctions = [
  "registerToNotificationTopic",
  "getMyTopics",
  "sendNotificationToTopic",
  "sendNotificationToUser",
  "changeUserClaims",
  "setMeAsAdmin",
  "updateFoodMenu",
  "createPost",
  "saveFileToBucket",
  "getFoodMenu",
  "getAlerts",
  "triggerCreateUser",
];

export default function AdminErrorsPage() {
  const navigate = useNavigate();

  const logList = ErrorLogService.LazyList();

  const { setShowSideMenuDrawer } = useMainState();

  const { pushAlert } = useModal();

  const [firstLoading, setFirstLoading] = React.useState(false);

  const handleFetch = async () => {
    try {
      setFirstLoading(true);
      await logList.runRequest();
    } catch (error: any) {
      console.log(...new Dev("handleFetch", error).Error());
      pushAlert({
        type: "error",
        message: "Erro ao obter histórico",
      });
    } finally {
      setFirstLoading(false);
    }
  };

  React.useEffect(() => {
    handleFetch();
  }, []);

  return (
    <Box
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
      }}
      pt={"30px"}
      pb={"45px"}
    >
      <Box width={"90%"}>
        <PageHeader
          title="Erros"
          leftIcon={<MenuRounded />}
          onLeftAction={() => setShowSideMenuDrawer(true)}
          rightIcon={<RefreshRounded />}
          onRightAction={() => logList.runRequest(true)}
        />

        <Box mt={4}>
          <Box
            mb={2}
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <MainText size="subtitle" color="black">
              Histórico
            </MainText>
          </Box>

          {/* List */}

          <Box>
            {firstLoading &&
              [90, 120, 100, 130].map((item) => (
                <Skeleton
                  key={item}
                  animation="wave"
                  variant="rounded"
                  width={"100%"}
                  height={item}
                  sx={{ borderRadius: "15px", mb: 2 }}
                />
              ))}

            {logList.items.map((item) => (
              <Box
                key={item.id}
                sx={{
                  border: `3px solid ${GreyPallet.Light}`,
                  borderRadius: "15px",
                  width: "100%",
                  overflow: "hidden",
                  marginBottom: "20px",
                  "& *": { boxSizing: "border-box", lineBreak: "anywhere" },
                }}
              >
                <Box
                  style={{
                    textAlign: "start",
                    width: "100%",
                  }}
                  py={1}
                >
                  <Box
                    px={2}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <MainText color="regular" size="small">
                      {DateTime.fromMillis(item.createdAtTimestamp).toFormat("dd/LL/y HH:mm")}
                    </MainText>
                  </Box>

                  <Divider
                    sx={{
                      my: 1,
                      border: `1px solid ${GreyPallet.Light}`,
                      borderRadius: "1px",
                    }}
                  />

                  <Box
                    px={2}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {!!item.user && (
                      <Box
                        sx={{
                          justifyContent: "space-between",
                          display: "flex",
                          alignItems: "center",
                          borderRadius: "5px",
                          width: "100%",
                          mb: 1,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <AccountCircleRounded sx={{ mr: 1 }} />
                          <MainText color="black" size="caption">
                            {item?.user?.email || "Auth Token"}
                          </MainText>
                        </Box>

                        <IconButton
                          sx={{ alignSelf: "end" }}
                          color="secondary"
                          onClick={() => navigate("/user/" + (item?.user as any)?.uid)}
                        >
                          <ArrowForwardRounded />
                        </IconButton>
                      </Box>
                    )}

                    <Box mb={2}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <DataObjectRounded sx={{ mr: 1 }} fontSize="small" />
                        <MainText color="black" size="small">
                          Dados:
                        </MainText>
                      </Box>

                      <Box mb={1} mt={1}>
                        <MainText color="black" size="caption">
                          <span style={{ color: GreyPallet.Regular }}>Erro:</span> {item.errorTitle}
                        </MainText>
                      </Box>

                      <Box mb={1} mt={1}>
                        <MainText color="black" size="caption">
                          <span style={{ color: GreyPallet.Regular }}>Tela:</span> {item.screen}
                        </MainText>
                      </Box>

                      <Box mb={1} mt={1}>
                        <MainText color="black" size="caption">
                          <span style={{ color: GreyPallet.Regular }}>Versão:</span> {item.version}
                        </MainText>
                      </Box>

                      <Box mb={1} mt={1}>
                        <MainText color="black" size="caption">
                          <span style={{ color: GreyPallet.Regular }}>Stack:</span>{" "}
                          {item.errorStack}
                        </MainText>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}

            {logList.hasMore && !firstLoading && (
              <MainSolidButton
                loading={logList.isLoading}
                text="Carregar Mais"
                icon={<KeyboardArrowDownRounded />}
                onClick={() => logList.runRequest()}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
